// src/pages/provider/ProviderDashboardPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Briefcase,
  Star,
  MessageCircle,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  User,
  Check
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/ProviderDashboardPage.module.css';

// Redux imports
import {
  getProviderById,
  selectCurrentProvider,
  selectProviderLoading,
  selectProviderError,
  clearCurrentProvider,
  clearErrors
} from '../../store/slices/providerSlice';
import {
  getReviewStatistics,
  selectReviewStatistics,
  selectReviewsLoading
} from '../../store/slices/serviceProviderReviewSlice';
import { selectUser, selectIsAuthenticated } from '../../store/slices/authSlice';
import { selectProfile } from '../../store/slices/profileSlice';

const ProviderDashboardPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);

  const { currentLanguage } = useLanguage();

  // Redux selectors
  const authUser = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const provider = useSelector(selectCurrentProvider);
  const profile = useSelector(selectProfile);
  const isLoading = useSelector(selectProviderLoading);
  const providerError = useSelector(selectProviderError);
  const reviewStats = useSelector(selectReviewStatistics);
  const reviewsLoading = useSelector(selectReviewsLoading);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: welcomeBack } = useTranslation('Welcome back,');
  const { translatedText: available } = useTranslation('Available');
  const { translatedText: unavailable } = useTranslation('Unavailable');
  const { translatedText: rating } = useTranslation('Rating');
  const { translatedText: reviews } = useTranslation('Reviews');
  const { translatedText: responseTime } = useTranslation('Response Time');
  const { translatedText: schedule } = useTranslation('Schedule');
  const { translatedText: recentActivity } = useTranslation('Recent Activity');
  const { translatedText: performanceMetrics } = useTranslation('Performance Metrics');
  const { translatedText: completionRate } = useTranslation('Completion Rate');
  const { translatedText: averageRating } = useTranslation('Average Rating');
  const { translatedText: responseTimeLabel } = useTranslation('Response Time');
  const { translatedText: providerTips } = useTranslation('Provider Tips');
  const { translatedText: tip1 } = useTranslation('Keep your availability updated to get more job requests');
  const { translatedText: tip2 } = useTranslation('Respond to client inquiries within 15 minutes');
  const { translatedText: tip3 } = useTranslation('Add photos of your work to showcase your skills');
  const { translatedText: tip4 } = useTranslation('Maintain a 4.5+ rating to get featured');
  const { translatedText: haitianServices } = useTranslation('Haitian Services');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: loadingProviderData } = useTranslation('Loading provider data...');
  const { translatedText: confirmed } = useTranslation('Confirmed');
  const { translatedText: pending } = useTranslation('Pending');
  const { translatedText: completed } = useTranslation('Completed');
  const { translatedText: viewAll } = useTranslation('View All');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // ======================================================
  // IS AVAILABLE - Check today's schedule
  // ======================================================
  const isAvailableToday = useCallback(() => {
    const providerData = profile?.providerProfile;
    
    if (!providerData?.availability) return false;
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    const todaySchedule = providerData.availability[today];
    
    return todaySchedule?.available === true;
  }, [profile]);

  // ======================================================
  // GET TODAY'S SCHEDULE
  // ======================================================
  const getTodaySchedule = useCallback(() => {
    const providerData = profile?.providerProfile;
    if (!providerData?.availability) return null;
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    
    return providerData.availability[today] || null;
  }, [profile]);

  // ======================================================
  // GET RATING
  // ======================================================
  const getRating = useCallback(() => {
    return provider?.rating || 0;
  }, [provider]);

  // ======================================================
  // GET TOTAL REVIEWS
  // ======================================================
  const getTotalReviews = useCallback(() => {
    return reviewStats?.totalReviews || provider?.totalReviews || 0;
  }, [reviewStats, provider]);

  // ======================================================
  // GET RESPONSE TIME
  // ======================================================
  const getResponseTime = useCallback(() => {
    return provider?.responseTime || 0;
  }, [provider]);

  // ======================================================
  // STATS DATA - ALL FROM REDUX
  // ======================================================
  const stats = [
    {
      id: '1',
      label: rating,
      value: getRating().toFixed(1),
      icon: Star,
      color: colors.accentGold,
      bg: colors.accentGold + '15'
    },
    {
      id: '2',
      label: reviews,
      value: getTotalReviews(),
      icon: MessageCircle,
      color: colors.secondary,
      bg: colors.secondary + '15'
    },
    {
      id: '3',
      label: responseTime,
      value: `${getResponseTime()}m`,
      icon: Clock,
      color: colors.tropicalGreen,
      bg: colors.tropicalGreen + '15'
    },
  ];

  // ======================================================
  // LOAD PROVIDER DATA
  // ======================================================
  const loadProviderData = useCallback(async () => {
    const providerId = profile?.providerProfile?._id || authUser?.id;
    if (!providerId) return;
    
    try {
      await dispatch(getProviderById({ id: providerId })).unwrap();
      await dispatch(getReviewStatistics(providerId)).unwrap();
    } catch (error) {
      console.error('Failed to load provider data:', error);
    }
  }, [dispatch, profile, authUser]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadProviderData();
    }
  }, [loadProviderData, isAuthenticated]);

  // Handle errors
  useEffect(() => {
    if (providerError) {
      console.error('Provider error:', providerError);
      dispatch(clearErrors());
    }
  }, [providerError, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCurrentProvider());
      dispatch(clearErrors());
    };
  }, [dispatch]);

  // ======================================================
  // RECENT ACTIVITIES
  // ======================================================
  const getRecentActivities = () => {
    const activities = [];
    
    if (provider?.reviews && provider.reviews.length > 0) {
      provider.reviews.slice(0, 3).forEach((review) => {
        activities.push({
          id: `review_${review._id || Date.now()}`,
          title: `${review.rating}⭐ review from ${review.customerName || 'Customer'}`,
          time: review.date ? new Date(review.date).toLocaleDateString() : 'Recently',
          type: 'review',
          status: 'completed'
        });
      });
    }

    // Only show if provider exists and has no activities
    if (activities.length === 0 && provider) {
      activities.push({
        id: '1',
        title: 'Profile created successfully',
        time: provider.createdAt ? new Date(provider.createdAt).toLocaleDateString() : 'Recently',
        type: 'update',
        status: 'completed'
      });
    }

    return activities;
  };

  // ======================================================
  // HELPER FUNCTIONS
  // ======================================================
  const getActivityIcon = (type) => {
    switch(type) {
      case 'request': return User;
      case 'review': return Star;
      case 'update': return Check;
      default: return AlertCircle;
    }
  };

  const getActivityColor = (type) => {
    switch(type) {
      case 'request': return colors.primary;
      case 'review': return colors.accentGold;
      case 'update': return colors.secondary;
      default: return colors.gray500;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return colors.warning;
      case 'completed': return colors.success;
      case 'confirmed': return colors.success;
      default: return colors.gray400;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return pending;
      case 'completed': return completed;
      case 'confirmed': return confirmed;
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const recentActivities = getRecentActivities();
  const isAvailable = isAvailableToday();
  const todaySchedule = getTodaySchedule();

  // ======================================================
  // LOADING STATE
  // ======================================================
  if (isLoading && !provider) {
    return (
      <div className={styles.providerDashboardContainer}>
        <div className={styles.dashboardHeader}>
          <h2>{dashboard}</h2>
        </div>
        <div className={styles.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">{loadingProviderData}</p>
        </div>
      </div>
    );
  }

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.providerDashboardContainer}>
      <div className={styles.dashboardHeader}>
        <h2>{dashboard}</h2>
        <div className={styles.dashboardHeaderRight}>
          <span 
            className={styles.availabilityBadge} 
            style={{ backgroundColor: isAvailable ? colors.success + '20' : colors.error + '20' }}
          >
            <span className={isAvailable ? styles.availabilityDot : styles.availabilityDotUnavailable} />
            <span className={styles.availabilityText} style={{ color: isAvailable ? colors.success : colors.error }}>
              {isAvailable ? available : unavailable}
            </span>
          </span>
          {todaySchedule && (
            <span className={styles.scheduleText}>
              {todaySchedule.start} - {todaySchedule.end}
            </span>
          )}
        </div>
      </div>

      <div className={styles.dashboardContent}>
        {/* Welcome Section */}
        <div className={styles.welcomeSection}>
          <div>
            <div className={styles.welcomeText}>{welcomeBack}</div>
            <div className={styles.providerName}>
              {profile?.providerProfile?.businessName || provider?.businessName || authUser?.name || 'Provider'}
            </div>
          </div>
          <div className={styles.availabilityBadge} style={{ backgroundColor: isAvailable ? colors.success + '15' : colors.error + '15' }}>
            <span className={isAvailable ? styles.availabilityDot : styles.availabilityDotUnavailable} />
            <span className={styles.availabilityText} style={{ color: isAvailable ? colors.success : colors.error }}>
              {isAvailable ? available : unavailable}
            </span>
          </div>
        </div>

        {/* Quick Stats - ALL REAL DATA */}
        <div className={styles.statsGrid}>
          {stats.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.id} className={styles.statCard}>
                <div className={styles.statIconContainer} style={{ backgroundColor: item.bg }}>
                  <Icon size={24} color={item.color} />
                </div>
                <div className={styles.statValue}>{item.value}</div>
                <div className={styles.statLabel}>{item.label}</div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className={styles.quickActionsContainer}>
          <button 
            className={styles.quickActionBtn} 
            style={{ backgroundColor: colors.secondary }}
            onClick={() => navigate('/provider-schedule')}
          >
            <Calendar size={24} color={colors.white} />
            <span className={styles.quickActionText}>{schedule}</span>
          </button>
        </div>

        {/* Recent Activity */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h6 className={styles.sectionTitle}>🔄 {recentActivity}</h6>
            <button className={styles.seeAllBtn}>{viewAll}</button>
          </div>
          {recentActivities.length > 0 ? (
            recentActivities.map((item) => {
              const Icon = getActivityIcon(item.type);
              const color = getActivityColor(item.type);
              const statusColor = getStatusColor(item.status);
              
              return (
                <div key={item.id} className={styles.activityItem}>
                  <div className={styles.activityIconContainer} style={{ backgroundColor: color + '15' }}>
                    <Icon size={20} color={color} />
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityTitle}>{item.title}</div>
                    <div className={styles.activityMeta}>
                      <span className={styles.activityTime}>{item.time}</span>
                      <span className={styles.activityStatus} style={{ 
                        backgroundColor: statusColor + '20',
                        color: statusColor
                      }}>
                        {getStatusLabel(item.status)}
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={20} color={colors.gray400} />
                </div>
              );
            })
          ) : (
            <div className={styles.noActivity}>
              <p>No recent activity</p>
            </div>
          )}
        </div>

        {/* Performance Metrics - ALL REAL DATA */}
        <div className={styles.performanceSection}>
          <h6 className={styles.performanceTitle}>📊 {performanceMetrics}</h6>
          <div className={styles.metricsContainer}>
            <div className={styles.metricItem}>
              <div className={styles.metricValue} style={{ color: colors.primary }}>
                {provider?.completionRate || 0}%
              </div>
              <div className={styles.metricLabel}>{completionRate}</div>
            </div>
            <div className={styles.metricDivider} />
            <div className={styles.metricItem}>
              <div className={styles.metricValue} style={{ color: colors.primary }}>
                {getRating().toFixed(1)}
              </div>
              <div className={styles.metricLabel}>{averageRating}</div>
            </div>
            <div className={styles.metricDivider} />
            <div className={styles.metricItem}>
              <div className={styles.metricValue} style={{ color: colors.primary }}>
                {getResponseTime()}m
              </div>
              <div className={styles.metricLabel}>{responseTimeLabel}</div>
            </div>
          </div>
        </div>

        {/* Provider Tips */}
        <div className={styles.tipsContainer}>
          <h6 className={styles.tipsTitle}>💡 {providerTips}</h6>
          {[tip1, tip2, tip3, tip4].map((tip, index) => (
            <div key={index} className={styles.tipItem}>
              <CheckCircle size={16} color={colors.success} />
              <span className={styles.tipText}>{tip}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProviderDashboardPage;